// Hook: loads the admin-driven uniform-supplier dropdown (db-46) from the suppliers
// table, cached in-memory across mounts (the wizard renders the uniform section once
// per child). Each option carries { name, kind, has_authority_doc } so the section
// drives the School Locker / reimbursement / authority behaviour off the supplier ROW,
// not a hardcoded name. Same cache pattern as useStandardCosts.
//
// TypeScript source — no .js stub needed; importers use the extensionless specifier.

import React from "react";
import { type DropdownSupplier, listUniformDropdownSuppliers } from "../data/suppliers";

let _cached: DropdownSupplier[] | null = null;
let _cacheTime = 0;
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

export function useUniformSuppliers() {
  const [suppliers, setSuppliers] = React.useState<DropdownSupplier[]>(_cached || []);
  const [isLoaded, setLoaded] = React.useState(!!_cached);

  React.useEffect(function () {
    if (_cached && Date.now() - _cacheTime < CACHE_TTL) {
      setSuppliers(_cached);
      setLoaded(true);
      return;
    }
    listUniformDropdownSuppliers().then(function (rows) {
      const list = rows || [];
      _cached = list;
      _cacheTime = Date.now();
      setSuppliers(list);
      setLoaded(true);
    });
  }, []);

  return { suppliers, isLoaded };
}

// Bust the cache after an admin edits/creates a supplier (so the wizard picks it up).
export function invalidateUniformSuppliersCache() {
  _cached = null;
  _cacheTime = 0;
}

// Resolve a stored supplier NAME to its capture-flow fields from the loaded list.
// Unknown / not-yet-loaded → empty kind (the section treats it as neutral: no special
// behaviour fires). Since db-50 the section derives its flow from payment_method +
// document_type (via deriveSupplierType), not the name.
export function supplierMeta(suppliers: DropdownSupplier[] | null | undefined, name: string) {
  const row = (suppliers || []).find((s) => s.name === name);
  return {
    kind: row ? row.kind : "",
    hasAuthorityDoc: row ? !!row.has_authority_doc : false,
    paymentMethod: row ? row.payment_method || "" : "",
    documentType: row ? row.document_type || "" : "",
    actionSheetOverride: row ? row.action_sheet_override || "" : "",
  };
}
